import { computed, ref } from 'vue'

import { defineStore } from 'pinia'
import { useSentry } from '@common/error-monitoring-layer/composables/useSentry'

import type { AuthResponse } from '../types/auth'

// COMMENTED OUT: Sentry import moved to composable level to avoid SSR state leaks
// import { useSentry } from '@common/error-monitoring-layer/composables/useSentry'

export enum AuthStatus {
  LOADING = 'LOADING',
  AUTHENTICATED = 'AUTHENTICATED',
  UNAUTHENTICATED = 'UNAUTHENTICATED',
}

export interface AuthState {
  contactId: string | null
  token: string | null
  isAuthenticated: boolean
  status: AuthStatus
  error: string | null
  showLoginModal: boolean
  modalPage: string
}

/**
 * Auth Store - Manages authentication state
 * Uses Composition API pattern for better reactivity
 */
export const useAuthStore = defineStore('auth', () => {
  // ===== STATE =====
  const contactId = ref<string | null>(null)
  const userId = ref<string | null>(null)
  const token = ref<string | null>(null)
  const isAuthenticated = ref<boolean>(false)
  const status = ref<AuthStatus>(AuthStatus.LOADING)
  const error = ref<string | null>(null)
  const showLoginModal = ref<boolean>(false)
  const modalPage = ref<string>('LOGIN')
  const isSSODisabled = ref<boolean>(false)
  /**
   * Holds the FULL `AuthResponse` from a deferred login (OTP / Google / email-password
   * with `deferSessionUntilInPlacePasswordSet: true`). Needed to persist the cookie
   * after `setPassword` succeeds when the set-password response itself does not
   * carry a fresh token. Cleared once the cookie is persisted or on logout.
   */
  const pendingAuthData = ref<AuthResponse | null>(null)

  // Sentry integration (client-side only)
  // COMMENTED OUT: Potential SSR state leak - moving to composable level
  // let sentryHelpers: ReturnType<typeof useSentry> | null = null
  // if (process.client) {
  //   try {
  //     sentryHelpers = useSentry()
  //   } catch {
  //     console.warn('[Auth Store] Sentry not available')
  //   }
  // }

  // ===== COMPUTED GETTERS =====
  const isLoggedIn = computed(() => isAuthenticated.value && !!contactId.value)
  const isChecking = computed(() => status.value === AuthStatus.LOADING)

  // ===== ACTIONS =====
  const setStatus = (newStatus: AuthStatus) => {
    status.value = newStatus
  }

  const setError = (newError: string | null) => {
    error.value = newError
  }

  const setAuth = (newToken: string, newUserId: string, newContactId?: string) => {
    token.value = newToken
    contactId.value = newContactId || null
    userId.value = newUserId
    isAuthenticated.value = true
    status.value = AuthStatus.AUTHENTICATED
    error.value = null

    // Update Sentry user context (client-side only)
    // Note: Server-side user context should be set per-request when capturing errors
    // to avoid mixing user data between concurrent requests
    // if (sentryHelpers) {
    //   try {
    //     sentryHelpers.setUser(newUserId, newContactId)
    //     console.log('[Auth Store] Sentry user context set')
    //   } catch (e) {
    //     console.warn('[Auth Store] Could not set Sentry user context', e)
    //   }
    // }
  }

  const setPendingAuthData = (data: AuthResponse | null) => {
    pendingAuthData.value = data
  }

  const clearAuth = () => {
    // Enhanced logging for SSR debugging

    token.value = null
    contactId.value = null
    isAuthenticated.value = false
    status.value = AuthStatus.UNAUTHENTICATED
    error.value = null
    pendingAuthData.value = null

    // Clear Sentry user context (client-side only)
    // COMMENTED OUT: Moving to composable level to avoid SSR state leaks
    // if (sentryHelpers) {
    //   try {
    //     sentryHelpers.clearUser()
    //     console.log('[Auth Store] Sentry user context cleared')
    //   } catch (e) {
    //     console.warn('[Auth Store] Could not clear Sentry user context', e)
    //   }
    // }
  }

  const setIsSSODisabled = (value: boolean) => {
    isSSODisabled.value = value
  }

  const openLoginModal = (page: string = 'LOGIN') => {
    showLoginModal.value = true
    modalPage.value = page
  }

  const closeLoginModal = () => {
    showLoginModal.value = false
    modalPage.value = 'LOGIN'
  }

  const setModalPage = (page: string) => {
    modalPage.value = page
  }

  // ===== PUBLIC API =====
  return {
    // State
    contactId,
    token,
    userId,
    isAuthenticated,
    status,
    error,
    showLoginModal,
    modalPage,
    pendingAuthData,

    // Computed getters
    isLoggedIn,
    isChecking,

    // Actions
    setStatus,
    setIsSSODisabled,
    setError,
    setAuth,
    setPendingAuthData,
    clearAuth,
    openLoginModal,
    closeLoginModal,
    setModalPage,
  }
})
